import {
  HistoryActors,
  withActorSuffix,
} from "./historyActors";
import {
  extractPayloadFromRequest,
  formatPayloadSummary,
} from "./formatPayloadSummary";

function sanitizeLegacyRequest(request: string): string {
  return request.replace(/speed\s+(\d+(?:\.\d+)?)%/gi, "speed $1");
}

function formatHeatPumpAttached(
  request: string,
  actors?: HistoryActors,
): string | null {
  const match = request.match(
    /Attached heat pump id:\s*(\S+)\s+to the device/i,
  );
  if (!match) return null;
  return withActorSuffix(`Heat pump linked (${match[1]})`, actors);
}

function formatDeviceLinked(
  request: string,
  actors?: HistoryActors,
): string | null {
  const userLabel = actors?.userDisplayName || "app user";

  const withUserId = request.match(
    /Attached device:\s*(\S+)\s+to the user id\s*:?\s*(\d+)/i,
  );
  if (withUserId) {
    return withActorSuffix(
      `Device ${withUserId[1]} linked to ${userLabel}`,
      actors,
    );
  }

  const withEmail = request.match(
    /Attached device:\s*(\S+)\s+to the user\s+(\S+)\.?/i,
  );
  if (withEmail) {
    const label = actors?.userDisplayName || withEmail[2];
    return withActorSuffix(`Device ${withEmail[1]} linked to ${label}`, actors);
  }

  const adminLinked = request.match(
    /Admin ID\s*:?\s*\d+\s+linked user ID\s+\d+\s+\(([^)]+)\)\s+to device\s+(\S+)/i,
  );
  if (adminLinked) {
    const label = actors?.userDisplayName || adminLinked[1];
    return withActorSuffix(
      `Device ${adminLinked[2]} linked to ${label}`,
      actors,
    );
  }

  const adminUpdated = request.match(
    /Admin ID\s*:?\s*\d+\s+updated user email:\s*(\S+)\s+with device ID:\s*\d+/i,
  );
  if (adminUpdated) {
    const label = actors?.userDisplayName || adminUpdated[1];
    return withActorSuffix(`Device linked to ${label}`, actors);
  }

  return null;
}

function formatDeviceUnlinked(
  request: string,
  actors?: HistoryActors,
): string | null {
  const userLabel = actors?.userDisplayName || "app user";

  const adminRemoved = request.match(
    /Admin ID\s*:?\s*\d+\s+removed device ID:\s*\d+\s+from user ID:\s*\d+/i,
  );
  if (adminRemoved) {
    return withActorSuffix(`Device unlinked from ${userLabel}`, actors);
  }

  const detachedIds = request.match(
    /Detached device id:\s*\d+\s+from the user id\s*:?\s*\d+/i,
  );
  if (detachedIds) {
    return withActorSuffix(`Device unlinked from ${userLabel}`, actors);
  }

  const detachedSerial = request.match(
    /Detached device:\s*(\S+)\s+from the user\s+(\S+)/i,
  );
  if (detachedSerial) {
    const label = actors?.userDisplayName || detachedSerial[2];
    return withActorSuffix(
      `Device ${detachedSerial[1]} unlinked from ${label}`,
      actors,
    );
  }

  const adminUnlinked = request.match(
    /Admin ID\s*:?\s*\d+\s+unlinked device from user ID\s+\d+\s+\(([^)]+)\)/i,
  );
  if (adminUnlinked) {
    const label = actors?.userDisplayName || adminUnlinked[1];
    return withActorSuffix(`Device unlinked from ${label}`, actors);
  }

  const dealerDeleted = request.match(
    /Dealer ID:\s*\d+\s+deleted link with device/i,
  );
  if (dealerDeleted) {
    return withActorSuffix("Dealer link removed", actors);
  }

  return null;
}

function formatDealerAdminLink(
  request: string,
  actors?: HistoryActors,
): string | null {
  const accepted = request.match(
    /Admin ID:\s*\d+\s+accepted pending link between Dealer ID:\s*\d+\s+and device:\s*(\S+)/i,
  );
  if (accepted) {
    return withActorSuffix(`Dealer link accepted for ${accepted[1]}`, actors);
  }

  const rejectedToAccepted = request.match(
    /Admin ID:\s*\d+\s+changed rejected link to accepted between Dealer ID:\s*\d+\s+and device:\s*(\S+)/i,
  );
  if (rejectedToAccepted) {
    return withActorSuffix(
      `Dealer link accepted for ${rejectedToAccepted[1]}`,
      actors,
    );
  }

  const created = request.match(
    /Admin ID:\s*\d+\s+created direct link between Dealer ID:\s*\d+\s+and device:\s*(\S+)/i,
  );
  if (created) {
    return withActorSuffix(`Dealer linked to device ${created[1]}`, actors);
  }

  const pending = request.match(
    /Dealer ID:\s*\d+\s+created pending link with device/i,
  );
  if (pending) return withActorSuffix("Dealer link requested", actors);

  return null;
}

function formatPortugueseSchedules(request: string): string | null {
  if (/Agendamentos alterados/i.test(request)) {
    return "Schedules updated";
  }
  return null;
}

/**
 * Convert known legacy free-text history requests into readable English.
 * Returns null when no known pattern matches.
 */
export function formatLegacyRequest(
  request: string,
  actors?: HistoryActors,
): string | null {
  const trimmed = request.trim();
  if (!trimmed) return null;

  const fromPayload = extractPayloadFromRequest(trimmed);
  if (fromPayload) {
    const summary = formatPayloadSummary(fromPayload);
    if (summary) return withActorSuffix(summary, actors);
  }

  return (
    formatHeatPumpAttached(trimmed, actors) ||
    formatDeviceLinked(trimmed, actors) ||
    formatDeviceUnlinked(trimmed, actors) ||
    formatDealerAdminLink(trimmed, actors) ||
    formatPortugueseSchedules(trimmed)
  );
}

export function sanitizeOrPassthrough(request: string): string {
  return sanitizeLegacyRequest(request);
}
